/**
 * 互动故事AI提示词
 * 专注于教会大模型理解互动故事的创作规范和最佳实践
 */
export class InteractiveStoryPrompt {
  
  /**
   * 生成专业的互动故事创作提示词
   */
  static generatePrompt(userOutline, genre, style) {
    return `# AVG对话驱动互动故事创作 - 精品7场景结构

基于用户创意: ${userOutline}
类型: ${genre} | 风格: ${style}

🎯 节点ID命名规范（必须严格遵守）：
- 主线节点：M1, M2, M3, M4 (Main line - 主线)
- 支线节点：L1, L2, L3 (Branch line - 分支)
- 结局节点：E1, E2, E3 (Ending - 结局)

🚨🚨🚨 重要：必须生成完整的7个场景，使用标准命名，一个场景都不能少！

**必须生成的场景ID列表（按顺序）：**
M1, M2, M3, M4, L1, E1, E2

📊 故事结构设计（7节点标准结构）：
- M1: 开场（主线开始）
- M2: 发展（主线继续）
- M3: 铺垫（主线）
- M4: 关键决策点（必须有且仅有2个choices，不多不少）
- L1: 分支A路径（从M4第一个选择进入）
- E1: 分支A结局（从L1到达）
- E2: 分支B结局（从M4第二个选择直达）

🚨 关键约束（违反则生成失败）：
1. M4节点必须有且仅有2个choices（不能是1个或3个）
2. M4的第一个choice必须指向L1（targetNodeId: "L1"）
3. M4的第二个choice必须指向E2（targetNodeId: "E2"）
4. L1节点无分支，choices数组为空，通过顺序连接到E1
5. 所有节点ID必须可达，不能有孤立节点
6. 结局节点（E1, E2）不能有choices（hasChoices: false，choices: []）
7. 每个场景包含3-5段对话
8. 场景描述30字内
9. 确保JSON格式正确

\`\`\`json
{
  "outline": {
    "title": "故事标题",
    "summary": "故事摘要(100字)",
    "mainPlot": "主线剧情(150字)",
    "mainConflict": "核心冲突(80字)"
  },
  "characters": [
    {
      "id": "char-001",
      "name": "角色名",
      "role": "protagonist|supporting|antagonist",
      "description": "角色描述",
      "languageStyle": "说话风格"
    }
  ],
  "chapters": [
    {
      "id": 1,
      "title": "第一章标题",
      "status": "completed",
      "scenes": [
        // ⚠️ 必须按顺序生成所有7个场景，使用M/L/E命名
        {"id": "M1", "title": "开始", "description": "30字内", "dialogues": [{"character": "主角", "content": "第一句对话", "emotion": "好奇"}, {"character": "引导者", "content": "第二句对话", "emotion": "神秘"}, {"character": "主角", "content": "第三句对话", "emotion": "思考"}], "choices": [], "hasChoices": false},
        {"id": "M2", "title": "探索", "description": "30字内", "dialogues": [{"character": "主角", "content": "对话内容", "emotion": "探索"}, {"character": "伙伴", "content": "对话内容", "emotion": "支持"}], "choices": [], "hasChoices": false},
        {"id": "M3", "title": "发现", "description": "30字内", "dialogues": [{"character": "主角", "content": "对话内容", "emotion": "惊讶"}, {"character": "神秘人", "content": "对话内容", "emotion": "警告"}], "choices": [], "hasChoices": false},
        {"id": "M4", "title": "关键选择", "description": "30字内", "dialogues": [{"character": "主角", "content": "现在我必须做出选择", "emotion": "纠结"}, {"character": "伙伴", "content": "无论如何我都支持你", "emotion": "坚定"}], "choices": [{"id": "choice-M4-1", "text": "勇敢面对", "targetNodeId": "L1", "path": "brave", "consequence": "进入战斗路径"}, {"id": "choice-M4-2", "text": "智慧化解", "targetNodeId": "E2", "path": "wisdom", "consequence": "直接达成和平结局"}], "hasChoices": true},
        {"id": "L1", "title": "勇敢路径", "description": "30字内", "dialogues": [{"character": "主角", "content": "我选择了勇气", "emotion": "坚定"}, {"character": "对手", "content": "那就来战斗吧", "emotion": "挑战"}], "choices": [], "hasChoices": false},
        {"id": "E1", "title": "勇者结局", "description": "30字内", "dialogues": [{"character": "主角", "content": "战斗胜利了", "emotion": "自豪"}, {"character": "伙伴", "content": "真正的英雄", "emotion": "赞赏"}], "choices": [], "hasChoices": false},
        {"id": "E2", "title": "智者结局", "description": "30字内", "dialogues": [{"character": "主角", "content": "智慧解决了问题", "emotion": "满足"}, {"character": "导师", "content": "这是最好的选择", "emotion": "欣慰"}], "choices": [], "hasChoices": false}
      ]
    }
  ]
}
\`\`\`

⚠️ 关键要求:
1. 🚨 必须生成所有7个场景，一个都不能少！scenes数组必须包含：M1, M2, M3, M4, L1, E1, E2
2. 每个场景的dialogues数组必须包含3-5段对话，不能为空
3. M4节点必须包含分支选择，严格限制为2个不同的选择路径（不能是1个或3个）
4. 分支A路径：M4 → L1 → E1 (选择+线性剧情+结局)
5. 分支B路径：M4 → E2 (选择+直接结局)
6. M1→M2→M3→M4 主线顺序连接，无需在choices中指定
7. 每个场景的description字段控制在30字以内
8. 确保JSON格式完整正确，不能缺失任何必需字段
9. 所有targetNodeId必须指向实际存在的场景ID

❌ 错误示例：
- 使用scene-1-1格式（必须用M/L/E格式）
- M4有1个或3个choices（必须是2个）
- targetNodeId指向不存在的节点
- 只生成部分场景

✅ 正确示例：
- 使用M/L/E命名格式
- M4严格2个choices
- 所有targetNodeId都指向存在的节点
- 连接关系：M1→M2→M3→M4→(L1→E1 或 E2)
- 生成完整的7个场景数组，包含1个分支点和2个结局路径`
  }
  
  /**
   * 生成JSON Schema验证规则
   */
  static getJSONSchema() {
    return {
      type: "object",
      required: ["outline", "characters", "chapters", "storyMetadata"],
      properties: {
        outline: {
          type: "object",
          required: ["title", "summary", "mainPlot", "mainConflict"],
          properties: {
            title: { type: "string", minLength: 5, maxLength: 50 },
            summary: { type: "string", minLength: 100, maxLength: 300 },
            mainPlot: { type: "string", minLength: 50, maxLength: 200 },
            mainConflict: { type: "string", minLength: 30, maxLength: 150 },
            themes: {
              type: "array",
              items: { type: "string" },
              minItems: 1,
              maxItems: 4
            }
          }
        },
        characters: {
          type: "array",
          minItems: 2,
          maxItems: 6,
          items: {
            type: "object",
            required: ["id", "name", "role", "description", "appearance", "background", "personality"],
            properties: {
              id: { type: "string", pattern: "^char-\\d{3}$" },
              name: { type: "string", minLength: 2, maxLength: 20 },
              role: { enum: ["protagonist", "supporting", "antagonist"] },
              description: { type: "string", minLength: 50, maxLength: 150 },
              appearance: { type: "string", minLength: 30, maxLength: 200 },
              background: { type: "string", minLength: 100, maxLength: 300 },
              personality: { type: "string", minLength: 30, maxLength: 150 },
              motivation: { type: "string" },
              arc: { type: "string" },
              relationships: { type: "string" },
              imagePrompt: { type: "string", minLength: 20 },
              languageStyle: { type: "string" }
            }
          }
        },
        chapters: {
          type: "array",
          minItems: 1,
          maxItems: 5,
          items: {
            type: "object",
            required: ["id", "title", "description", "status", "scenes"],
            properties: {
              id: { type: "number" },
              title: { type: "string", minLength: 5, maxLength: 30 },
              description: { type: "string", minLength: 20, maxLength: 200 },
              status: { enum: ["completed", "outline"] },
              scenes: {
                type: "array",
                minItems: 1,
                items: {
                  type: "object",
                  required: ["id", "title", "description", "content", "imagePrompt", "hasChoices", "background", "mood", "timeOfDay", "location"],
                  properties: {
                    id: { type: "string", pattern: "^(M|L|E)\\d+$" },
                    title: { type: "string", minLength: 3, maxLength: 30 },
                    description: { type: "string", minLength: 10, maxLength: 100 },
                    content: { type: "string", minLength: 500, maxLength: 2000 },
                    imagePrompt: { type: "string", minLength: 20 },
                    hasChoices: { type: "boolean" },
                    choices: {
                      type: "array",
                      items: {
                        type: "object",
                        required: ["id", "text", "targetNodeId", "path", "consequence"],
                        properties: {
                          id: { type: "string" },
                          text: { type: "string", minLength: 10, maxLength: 80 },
                          targetNodeId: { type: "string" },
                          path: { type: "string" },
                          consequence: { type: "string" },
                          emotionalImpact: { type: "string" }
                        }
                      }
                    },
                    dialogues: {
                      type: "array",
                      items: {
                        type: "object",
                        required: ["character", "content", "emotion"],
                        properties: {
                          character: { type: "string" },
                          content: { type: "string", minLength: 5 },
                          emotion: { enum: ["angry", "sad", "happy", "surprised", "fear", "disgust", "neutral"] },
                          subtext: { type: "string" }
                        }
                      }
                    },
                    background: { type: "string", minLength: 10 },
                    mood: { type: "string" },
                    timeOfDay: { type: "string" },
                    location: { type: "string", minLength: 5 },
                    keyEvents: {
                      type: "array",
                      items: { type: "string" }
                    },
                    stateChanges: {
                      type: "object",
                      properties: {
                        characterMood: { type: "string" },
                        relationships: { type: "string" },
                        worldState: { type: "string" }
                      }
                    }
                  }
                }
              }
            }
          }
        },
        storyMetadata: {
          type: "object",
          required: ["estimatedPlayTime", "difficultyLevel", "emotionalTone", "targetAudience"],
          properties: {
            estimatedPlayTime: { type: "string" },
            difficultyLevel: { enum: ["simple", "moderate", "complex"] },
            emotionalTone: { type: "string" },
            targetAudience: { type: "string" },
            contentWarnings: {
              type: "array",
              items: { type: "string" }
            }
          }
        }
      }
    }
  }
  
  /**
   * 生成创作指导文档
   */
  static getCreationGuidelines() {
    return {
      title: "互动故事创作指南",
      sections: [
        {
          title: "选择设计原则",
          content: [
            "每个选择都应该反映不同的价值观或策略",
            "避免明显的'正确'和'错误'选择",
            "选择的后果应该在后续场景中得到体现",
            "每个决策点严格限制为2个选择，避免选择困难"
          ]
        },
        {
          title: "角色塑造要点",
          content: [
            "给每个角色独特的语言风格和说话习惯",
            "角色的行为应该符合其背景和性格设定",
            "避免脸谱化，给角色添加复杂性和矛盾性",
            "角色间的关系应该推动故事发展"
          ]
        },
        {
          title: "场景构建技巧",
          content: [
            "开头要迅速建立情境和氛围",
            "中间要有冲突或转折点",
            "结尾要为下一场景或选择做铺垫",
            "环境描述要服务于情节和情感"
          ]
        },
        {
          title: "分支逻辑设计",
          content: [
            "主线剧情要清晰，支线要有意义",
            "不同路径最终可以汇聚到关键节点",
            "重要选择的影响要延续到后续章节",
            "避免死胡同式的分支设计"
          ]
        }
      ]
    }
  }
}
